import { inject, injectable } from "inversify";
import { action, makeObservable, observable, reaction, runInAction } from "mobx";
import { backgroundClient } from "@whatslly/commons/background/client";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { contentBackend } from "@whatslly/commons/contentBackend";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { IMUserStore } from "../IMUserStore";
import { MessageScraper } from "./messageScraper";

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType || "audio/mpeg" });
}

@injectable()
export class WaNotificationToneService {
  private toneModule: { playNotification: () => unknown } | undefined;
  private originalPlay: (() => unknown) | undefined;
  private readonly logger;

  /** Cached object URL; cleared by {@link invalidatePlaybackCache} or user update reaction. */
  cachedObjectUrl: string | undefined = undefined;

  constructor(
    @inject(FrontLogger) frontLogger: FrontLogger,
    @inject(IMUserStore) private readonly imUserStore: IMUserStore
  ) {
    this.logger = frontLogger.getLogger("wa-notification-tone-service");
    makeObservable(this, {
      cachedObjectUrl: observable,
      invalidatePlaybackCache: action,
      restoreDefaultNotificationTone: action,
      ensureCustomNotificationTonePatch: action,
    });

    reaction(
      () => this.imUserStore.user,
      () => {
        this.invalidatePlaybackCache();
      }
    );
  }

  init(_: WhatsappSelectorsType): void {
    contentBackend.on(ContentCommands.CONTENT_READY, () => {
      if (!MessageScraper.whatsappAPI?.WAWebNotificationTone) {
        this.logger.warn("WAWebNotificationTone missing after inject");
        return;
      }
      this.applyTonePatch(MessageScraper.whatsappAPI.WAWebNotificationTone);
    });
  }

  /** Drop cached blob URL so the next notification reloads audio from host storage. */
  invalidatePlaybackCache(): void {
    runInAction(() => {
      if (this.cachedObjectUrl) {
        try {
          URL.revokeObjectURL(this.cachedObjectUrl);
        } catch {
          // ignore
        }
        this.cachedObjectUrl = undefined;
      }
    });
  }

  /** After custom sound is removed: clear cache and hand `playNotification` back to WhatsApp's default tone. */
  restoreDefaultNotificationTone(): void {
    this.invalidatePlaybackCache();
    if (this.toneModule && this.originalPlay) {
      this.toneModule.playNotification = this.originalPlay;
    }
  }

  /** Re-hook custom playback after upload/sync (e.g. if {@link restoreDefaultNotificationTone} ran earlier). */
  ensureCustomNotificationTonePatch(): void {
    if (!this.toneModule || !this.originalPlay) {
      return;
    }
    this.toneModule.playNotification = () => {
      this.playCustomNotification();
    };
  }

  private applyTonePatch(toneModule: { playNotification: () => unknown }): void {
    this.toneModule = toneModule;
    if (this.originalPlay === undefined) {
      this.originalPlay = toneModule.playNotification?.bind(toneModule);
    }
    toneModule.playNotification = () => {
      this.playCustomNotification();
    };
  }

  private playCustomNotification(): void {
    void this.playCustomNotificationAsync().catch((err) => {
      this.logger.warn({ err }, "custom WA notification playback failed");
      void Promise.resolve(this.originalPlay?.());
    });
  }

  private async playCustomNotificationAsync(): Promise<void> {
    let url = this.cachedObjectUrl;
    if (!url) {
      const blob = await this.fetchBlobFromHost();
      if (!blob) {
        await Promise.resolve(this.originalPlay?.());
        return;
      }
      runInAction(() => {
        this.cachedObjectUrl = URL.createObjectURL(blob);
      });
      url = this.cachedObjectUrl;
    }
    if (!url) {
      await Promise.resolve(this.originalPlay?.());
      return;
    }
    try {
      await new Audio(url).play();
    } catch (err) {
      this.logger.warn({ err }, "failed to play custom WA notification sound");
      await Promise.resolve(this.originalPlay?.());
    }
  }

  /** Loads audio via background (`WA_NOTIFICATION_SOUND_GET_PAYLOAD` → S3-backed API). */
  private async fetchBlobFromHost(): Promise<Blob | null> {
    const res = await backgroundClient.send(BgCommands.WA_NOTIFICATION_SOUND_GET_PAYLOAD);
    if (!res || !res.base64) {
      return null;
    }
    return base64ToBlob(res.base64, res.mimeType);
  }
}
